import { HttpClient, HttpHeaders } from "@angular/common/http";
import { inject, Injectable, signal, computed } from "@angular/core";
import { takeUntilDestroyed, toObservable } from "@angular/core/rxjs-interop";
import { firstValueFrom } from "rxjs";
import { AppSettingsService } from "@shared/app-settings-service";
import { BroadcastService } from "@shared/broadcast-service";
import { CryptoService } from "@shared/crypto-service";
import { IframeMessage, IframeMessagesService } from "@shared/iframe-messages-service";
import { EncryptedPayload, SessionData } from "./auth.interfaces";
import { toSessionData } from "./session-data.mapper";
import { SessionKeepaliveService } from "./session-keepalive.service";

export interface CurrentUser {
    sub: string;
    name: string;
    given_name: string;
    family_name: string;
    locale: string;
    email: string;
    firm_id: string;
    csrf: string;
    sid: string;
    ifirm_userid: string;
    ifirm_url: string;
    ifirm_session_name?: string;
    role?: string | string[];
    tracking_licence_enabled: string;
    can_manage_general_settings: string;
    can_manage_dashboards_management_settings: string;
    can_delete_cards: string;
    can_bulk_change_cards: string;
    can_print_export: string;
    can_contacts_view_add_edit: string;
    can_edit_assignee: string;
    can_contacts_edit_group: string;
    can_contacts_edit_generic_informations: string;
    dashboard_list: string[];
    can_client_portal_admin: string;
    can_jobs_view_my: string;
    can_jobs_view_all: string;
    can_jobs_view_add_edit: string;
    can_jobs_view_edit_all_job_resources: string;
    can_jobs_view_edit_my_job_resources: string;
    can_workflow_resource: string;
    summary_view_new_ui_enabled: string;
    can_dashboard_management_create: string;
    have_rights_dashboard_list: string[];
}

@Injectable({ providedIn: "root" })
export class AuthService {
    private readonly http = inject(HttpClient);
    private readonly appSettingsService = inject(AppSettingsService);
    private readonly iframeMessagesService = inject(IframeMessagesService);
    private readonly keepalive = inject(SessionKeepaliveService);

    private currentUserPromise: Promise<CurrentUser> | undefined;
    private _currentUser = signal<CurrentUser | null>(null);
    public currentUser = this._currentUser.asReadonly();

    private sessionDataPromise: Promise<SessionData> | undefined;
    public sessionData = signal<SessionData | null>(null);
    public readonly sessionData$ = toObservable(this.sessionData);

    private filePathKey: string | undefined;
    private userInfoKey: string | undefined;
    public inReloginPrompted = signal<boolean>(false);
    public readonly reloginPrompted$ = toObservable(this.inReloginPrompted);

    public readonly currentFirmId = computed(() => this.currentUser()?.firm_id);

    constructor() {
        BroadcastService.on<IframeMessage>("reloginPrompted")
            .pipe(takeUntilDestroyed())
            .subscribe(() => {
                this.iframeMessagesService.postMessage("reloginPromptedAck", null);
                this.inReloginPrompted.set(true);
            });

        BroadcastService.on<IframeMessage>("sessionChanged")
            .pipe(takeUntilDestroyed())
            .subscribe((message) => {
                const session = this.sessionData();
                if (session) {
                    // Replace the object so the signal actually notifies; mutating in place did not.
                    this.sessionData.set({ ...session, sessionName: message.data.sessionName as string });
                }
                this.inReloginPrompted.set(false);
            });
    }
    private async getApiUrl() {
        const settings = await this.appSettingsService.getAppSettings();
        const url = settings?.AuthService_URL ?? "";
        const version = settings?.AuthService_Version ?? "";
        return url + version;
    }

    async getFilePathKey(): Promise<string> {
        if (!this.filePathKey) {
            const request = this.http.get((await this.getApiUrl()) + "key/filePathKey", {
                responseType: "text",
            });
            this.filePathKey = await firstValueFrom(request);
        }
        return this.filePathKey;
    }

    async getUserInfoKey(): Promise<string> {
        if (!this.userInfoKey) {
            const request = this.http.get((await this.getApiUrl()) + "key/userInfoKeyV1", {
                responseType: "text",
            });
            this.userInfoKey = await firstValueFrom(request);
        }
        return this.userInfoKey;
    }

    private async getSession() {
        if (!this.sessionDataPromise) {
            this.sessionDataPromise = this.initSession();
        }
        await this.sessionDataPromise;

        if (this.inReloginPrompted()) {
            // TODO is this the right approach?
            await firstValueFrom(BroadcastService.on<IframeMessage>("sessionChanged"));
        }
        await this.keepalive.ensureFresh();
        return this.sessionData();
    }

    private async initSession() {
        const result = await this.iframeMessagesService.postMessageResp("initSession", null, "initSessionInfo");
        const sessionData = toSessionData(result as Record<string, unknown>);
        this.sessionData.set(sessionData);
        this.keepalive.register(
            () => this.sessionData(),
            (session) => this.sessionData.set(session),
        );
        return sessionData;
    }

    // TODO at some point, migrate to crypto service
    async decryptUserInfo(toDecrypt: string) {
        let result = "";
        if (toDecrypt.startsWith("v001~")) {
            toDecrypt = toDecrypt.substring(5);
        }
        if (result === "") {
            const hashedKey = await CryptoService.sha256(await this.getUserInfoKey());
            const bytes = CryptoService.base64ToBytes(toDecrypt);
            const key = await window.crypto.subtle.importKey("raw", hashedKey, "AES-GCM", true, ["decrypt"]);
            const nonceLength = (bytes[3] << 24) | (bytes[2] << 16) | (bytes[1] << 8) | bytes[0];
            const nonce = bytes.subarray(4, 4 + nonceLength);
            const tagLength =
                (bytes[7 + nonceLength] << 24) | (bytes[6 + nonceLength] << 16) | (bytes[5 + nonceLength] << 8) | bytes[4 + nonceLength];
            const tag = bytes.subarray(8 + nonceLength, 8 + nonceLength + tagLength);
            const theRest = bytes.subarray(8 + nonceLength + tagLength);
            const cipherContent = CryptoService.concat(theRest, tag);
            const algoEncrypt = {
                name: "AES-GCM",
                iv: nonce,
                tagLength: tagLength * 8,
            };
            const decrypted = await crypto.subtle.decrypt(algoEncrypt, key, cipherContent);
            const decoder = new TextDecoder("utf-8");
            result = decoder.decode(decrypted);
        }
        return result;
    }

    async getCurrentUser() {
        if (!this.currentUserPromise) {
            this.currentUserPromise = this.getUserInfoInternal();
        }
        return this.currentUserPromise;
    }

    public setCurrentUser(user: CurrentUser) {
        this._currentUser.set(user);
    }

    private async getUserInfoInternal() {
        const result = await firstValueFrom(this.http.get<EncryptedPayload>((await this.getApiUrl()).replace("/v2", "/v3") + "connect/userinfo"));
        const decryptedResult = await this.decryptUserInfo(result.payload);
        const decryptedresultjson = JSON.parse(decryptedResult);
        if (typeof decryptedresultjson.role === "string") {
            decryptedresultjson.role = [decryptedresultjson.role];
        }
        this._currentUser.set(decryptedresultjson);
        return decryptedresultjson;
    }

    connectedUserCanManageSettings() {
        return this.currentUser()?.can_manage_general_settings == "True" || this.currentUser()?.can_manage_dashboards_management_settings == "True";
    }

    canManageGeneralSettings() {
        return this.currentUser()?.can_manage_general_settings == "True";
    }

    canManageDashboardManagementSettings() {
        return this.currentUser()?.can_manage_dashboards_management_settings == "True";
    }

    hasSecurityLevel(expectedRole: keyof CurrentUser): boolean {
        const currentUser = this.currentUser();
        if (!currentUser) {
            return false;
        }
        return currentUser[expectedRole] == "True";
    }

    public async addSingleSessionHeader(headers: HttpHeaders) {
        const sessionData = await this.getSession();
        if (sessionData) {
            return headers.set("x-site", sessionData.site).set("x-userid", sessionData.userId).set("x-session-name", sessionData.sessionName);
        } else {
            return headers;
        }
    }
}
